import { SupportedSubjects } from 'types/Subject';

const STORAGE_KEY = `${process.env.REACT_APP_VERSION ?? 'v1'}_sheets_timer`;

export type PersistedTimerSession = {
  elapsedSeconds: number;
  savedAt: number;
  isManuallyPaused: boolean;
};

type PersistedSheetsTimerStoreV2 = {
  durations: Record<string, number | null>;
  sessions: Record<string, PersistedTimerSession>;
};

type LegacyPersistedSheetsTimerStore = Record<string, number | null>;

const isLegacyStore = (value: unknown): value is LegacyPersistedSheetsTimerStore =>
  value != null && typeof value === 'object' && !('durations' in value) && !('sessions' in value);

const readStore = (): PersistedSheetsTimerStoreV2 => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return { durations: {}, sessions: {} };
    }

    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') {
      return { durations: {}, sessions: {} };
    }

    if (isLegacyStore(parsed)) {
      return { durations: parsed, sessions: {} };
    }

    const store = parsed as Partial<PersistedSheetsTimerStoreV2>;
    return {
      durations: store.durations && typeof store.durations === 'object' ? store.durations : {},
      sessions: store.sessions && typeof store.sessions === 'object' ? store.sessions : {},
    };
  } catch {
    return { durations: {}, sessions: {} };
  }
};

const writeStore = (store: PersistedSheetsTimerStoreV2): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Ignore quota / private mode errors.
  }
};

const buildSessionKey = (subjectCode: SupportedSubjects, sheetKey: string): string => `${subjectCode}|${sheetKey}`;

export const resolveElapsedFromSession = (session: PersistedTimerSession): number => {
  if (session.isManuallyPaused) {
    return session.elapsedSeconds;
  }

  const deltaSeconds = Math.floor((Date.now() - session.savedAt) / 1000);
  return session.elapsedSeconds + Math.max(0, deltaSeconds);
};

export const loadSheetsTimerDuration = (subjectCode: SupportedSubjects): number | null | undefined => {
  const persisted = readStore().durations[subjectCode];
  if (persisted === undefined) {
    return undefined;
  }

  return persisted;
};

export const saveSheetsTimerDuration = (subjectCode: SupportedSubjects, durationMinutes: number | null): void => {
  const store = readStore();
  store.durations[subjectCode] = durationMinutes;
  writeStore(store);
};

export const resolveSheetsTimerDuration = (subjectCode: SupportedSubjects): number | null => {
  const persisted = loadSheetsTimerDuration(subjectCode);
  if (persisted !== undefined) {
    return persisted;
  }

  return null;
};

export const loadSheetsTimerSession = (subjectCode: SupportedSubjects, sheetKey: string): PersistedTimerSession | null => {
  const session = readStore().sessions[buildSessionKey(subjectCode, sheetKey)];
  if (!session || typeof session.elapsedSeconds !== 'number' || typeof session.savedAt !== 'number') {
    return null;
  }

  return session;
};

export const saveSheetsTimerSession = (
  subjectCode: SupportedSubjects,
  sheetKey: string,
  session: PersistedTimerSession,
): void => {
  const store = readStore();
  store.sessions[buildSessionKey(subjectCode, sheetKey)] = session;
  writeStore(store);
};

export const clearSheetsTimerSession = (subjectCode: SupportedSubjects, sheetKey: string): void => {
  const store = readStore();
  delete store.sessions[buildSessionKey(subjectCode, sheetKey)];
  writeStore(store);
};
